import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { ContentMessage, PageHeader, SearchBar } from 'axway-react-ui';
import Fuse from 'fuse.js';
import { copyToClipboard } from 'components/utility/utils';
import CredentialCardBack from './CredentialCardBack';
import CredentialCardFlip from './CredentialCardFlip';
import { isSameToken, canAuthorize, isAuthorized, isImageDataURI } from './utils';
import CredentialCard from './CredentialCard';
import OAuthDialog from './OAuthDialog';
import Toast from './Toast';

class CredentialsView extends React.Component {
	constructor(props) {
		super(props);
		this.setSearchFilter = this.setSearchFilter.bind(this);
		this.onDialogClose = this.onDialogClose.bind(this);
		this.copyTokenToClipboard = this.copyTokenToClipboard.bind(this);
		IntlComponent.init(this);

		this.fuseOpts = {
			threshold: 0.1,
			distance: 1000,
			keys: [ 'name', 'type' ]
		};

		this.fuse = new Fuse(props.credentials, this.fuseOpts);
		this.state = {
			searchFilter: null,
			oauth: null,
			newlyAuthenticated: [],
			flippableCards: {}
		};
	}

	/**
	 * When receiving new props, update the Fuse search
	 * and check if there are any newly authenticated credentials
	 * that we will want to display.
	 */
	componentWillUpdate(props) {
		this.fuse = new Fuse(props.credentials, this.fuseOpts);

		// Get the newly authenticated credentials (for toast)
		this.state.newlyAuthenticated = props.credentials
			.filter(newCred => newCred.type === 'oauth2' && isAuthorized(newCred))
			.filter(newCred => {
				const oldCred = this.props.credentials.find(cred => cred.name === newCred.name);
				return !oldCred || !isSameToken(oldCred, newCred);
			})
			.map(c => c.name);

		// Show the toast notification for all newly authenticated credential cards
		if (this.state.newlyAuthenticated && this.state.newlyAuthenticated.length) {
			const msg = this.intl.formatMsg('{credentials} successfully authorized and tokens stored in memory only.', { credentials: this.state.newlyAuthenticated.join(',') });
			const time = 5000;
			this.props.onShowToast && this.props.onShowToast(time, msg);
		}

		// Update the state of all cards which can be flipped - type oauth2.
		if (Object.keys(this.state.flippableCards).length === 0
			&& this.state.flippableCards.constructor === Object) {
			this.state.flippableCards = props.credentials
				.filter(newCred => newCred.type === 'oauth2' && isAuthorized(newCred))
				.map(c => c.name)
				.reduce((result, field) => {
					result[field] = false;
					return result;
				}, {});
		}
	}

	/**
	 * When leaving the credential page we should make sure the state of Toast was updated
	 */
	componentWillUnmount() {
		this.props.onHideTost && this.props.onHideTost();
	}

	/**
	 * Set the search filter to limit the credentials displayed.
	 */
	setSearchFilter(filter = '') {
		this.setState({
			searchFilter: filter.trim()
		});
	}

	/**
	 * Copies the access or refresh token to clipboard and invoke toast notification.
	 */
	copyTokenToClipboard(token) {
		const result = copyToClipboard(token);
		if (result && token) {
			const msg = this.intl.formatMsg('Successfully copied the token to the clipboard.');
			const time = 1500;
			this.props.onShowToast && this.props.onShowToast(time, msg);
		}
	}

	/**
	 * Filter the credentials.
	 */
	applySearchFilter(items) {
		if (this.state.searchFilter && items) {
			return this.fuse.search(this.state.searchFilter);
		}
		return items;
	}

	/**
	 * Return an array of the actions available for the credential.
	 */
	actions(credential) {
		const actions = [];
		if (canAuthorize(credential)) {
			actions.push({
				label: isAuthorized(credential)
					? this.intl.formatMsg('Reauthorize') : this.intl.formatMsg('Authorize'),
				action: () => {
					this.setState({
						oauth: credential
					});
				}
			});
		}
		return actions;
	}
	/**
	 * Return an array of the actions specific for credential type oauth2.
	 */
	tokens(credential) {
		const tokens = [];
		if (credential.type === 'oauth2' && isAuthorized(credential)) {
			tokens.push({
				label: this.intl.formatMsg('Tokens'),
				action: (event) => {
					event.preventDefault();
					const flippableCards = { ...this.state.flippableCards };
					flippableCards[credential.name] = !flippableCards[credential.name];
					this.setState({
						flippableCards: flippableCards
					});
				}
			});
		}
		return tokens;
	}

	/**
	 * If oauth is triggered then display the OAuth Dialog component.
	 */
	oauthDialog() {
		const { oauth } = this.state;
		if (oauth) {
			return (
				<OAuthDialog
					credential={oauth}
					onClose={this.onDialogClose}
				/>
			);
		}
		return null;
	}
	/**
	 * Returns all cards by setting oauth2 authorized cards to be flippable.
	 */
	getCards(cred) {
		if (cred.type === 'oauth2' && isAuthorized(cred)) {
			return (
				<div className="flippable-card" key={cred.name}>
					<CredentialCardFlip
						isFlipped={
							this.state.flippableCards && this.state.flippableCards[cred.name]
						}
					>
						<div key="front">
							<CredentialCard
								key={cred.name}
								credential={{
									name: cred.name,
									type: cred.type,
									flow: cred.flow,
									icon: isImageDataURI(cred.icon) ? cred.icon : 'icon-GG-key',
									status: cred.status
								}}
								tokensActions={this.tokens(cred)}
								actions={this.actions(cred)}
							/>
						</div>
						<div key="back">
							<CredentialCardBack
								key={cred.name}
								credential={{
									name: cred.name,
									icon: isImageDataURI(cred.icon) ? cred.icon : 'icon-GG-key'
								}}
								tokensActions={this.tokens(cred)}
								tokens={{
									access: cred.access_token,
									refresh: cred.refresh_token
								}}
								copyTokenToClipboard={this.copyTokenToClipboard}
							/>
						</div>
					</CredentialCardFlip>
				</div>
			);
		} else {
			return (
				<CredentialCard
					key={cred.name}
					credential={{
						name: cred.name,
						type: cred.type,
						flow: cred.flow,
						icon: isImageDataURI(cred.icon) ? cred.icon : 'icon-GG-key',
						status: cred.status
					}}
					actions={this.actions(cred)}
				/>);
		}
	}

	onDialogClose() {
		this.setState({
			oauth: null
		});
		this.props.onRefresh && this.props.onRefresh();
	}

	renderEmpty(msg) {
		return (<ContentMessage
			detail={msg}
			imgClass="gg icon-GG-empty-list"
			className="sm empty"
			inlineContent
		/>);
	}

	render() {
		const { searchFilter } = this.state;
		const { toast } = this.props;
		const credentials = this.applySearchFilter(this.props.credentials);
		const cards = credentials
			.sort((c1, c2) => c1.name.localeCompare(c2.name))
			.map(cred => this.getCards(cred));
		const emptyMsg = searchFilter
			? this.renderEmpty(this.intl.formatMsg('No credentials matched \'{search}\'.', { search: searchFilter }))
			: this.renderEmpty(this.intl.formatMsg('No credentials configured.'));

		return (
			<div className="credentials-view">
				<PageHeader>
					<PageHeader.Section align="left">
						<PageHeader.Title
							infoPopover={{
								id: 'credentials-info',
								description: this.intl.formatMsg('A credential can be used to grant your service authorization to connect to another service. API Builder supports API Key, HTTP Basic and OAuth 2.0 based credentials. OAuth 2.0 credentials using the Authorization Code grant can be temporarily authorized in the UI but this authorization expires when the server is restarted.'),
								docLink: window.$conf.docCredentialUrl
							}}
						>
							{this.intl.formatMsg('Credentials')}
						</PageHeader.Title>
					</PageHeader.Section>
					<PageHeader.Section>
						<SearchBar
							onChange={this.setSearchFilter}
							throttle={50}
						/>
					</PageHeader.Section>
					<PageHeader.Section align="right" />
				</PageHeader>
				<div className="detail">
					{ toast && toast.show && (
						<Toast
							time={toast.time}
							onClose={this.props.onHideTost}
						>
							{toast.message}
						</Toast>
					)}
					<div className="cards">
						{cards.length ? cards : emptyMsg }
					</div>
				</div>
				{ this.oauthDialog() }
			</div>
		);
	}
}

CredentialsView.propTypes = {
	/**
	 * The credentials configured on the service.
	 */
	credentials: PropTypes.array.isRequired,

	/**
	 * The callback to refresh credential status.
	 */
	onRefresh: PropTypes.func.isRequired
};

export default injectIntl(CredentialsView);
